import React, { useMemo, useState } from "react"
import { z } from "zod"
import {
  ButtonCfg,
  ButtonConfig,
  ConfigurableButton,
  ConfirmationConfig,
  DropdownCfg,
  HeaderCfg,
  isButton,
  isDropdown,
  isHeader,
  isSeparator,
  isSpacer,
} from "./action-button-schema"
import {
  ConfirmationDialog,
  ConfirmationPopover,
  DropdownToolButton,
  DropdownToolItem,
  DropdownToolSubmenu,
  HorizontalSeparator,
  SizeVariant,
  SizeVariantContext,
  ToolButton,
  ToolHeader,
  VerticalSeparator,
} from "./action-button-primitives"
import { PublishedDict, usePubsubTopics } from "../../redux/pubsub"
import { getPlaceholderTopics, resolveTemplateString, resolveTemplateStringAndLogErrors } from "../../helpers/resolve-template-string"
import { mergeClasses } from "../../helpers/styling"
import { DotsHorizontalIcon } from "@radix-ui/react-icons"
import { useWidgetColorsCssVars } from "../../components/styling/use-widget-styling"
import { useMutation, UseMutationResult } from "@tanstack/react-query"

type ConfirmDialogState = {
  title?: string
  config: ConfirmationConfig
  onConfirmed: () => void
}

export function ActionButtonRenderer<T extends z.ZodTypeAny>(props: {
  variant: SizeVariant
  buttons: ButtonConfig<T>[]
  dispatch: (actions: z.infer<T>[]) => Promise<void> | void
  shouldHideButton?: (btn: ButtonConfig<T>) => boolean
  shouldDisableButton?: (btn: ButtonConfig<T>) => boolean
  expressionVars?: Record<string, string>
  popoverContentClassName?: string
  popoverContentStyle?: React.CSSProperties
  asToolbar?: boolean
}): React.ReactNode {
  const [ currentConfirmation, setCurrentConfirmation ] = useState<ConfirmDialogState | null>(null)
  const templateTopics = useMemo(() => collectTemplateTopics(props.buttons), [ props.buttons ])
  const componentVars = usePubsubTopics(templateTopics)
  const vars = useMemo(
    () => ({ ...componentVars, ...props.expressionVars }),
    [ componentVars, props.expressionVars ])

  const shouldHideButton = (button: ButtonConfig<T>) => {
    const hidden = "hidden" in button ? button.hidden : undefined
    if (hidden != null && evaluateBooleanExpression(hidden, vars)) return true
    if (props.shouldHideButton && props.shouldHideButton(button)) return true
    return false
  }

  const shouldDisableButton = (button: ButtonConfig<T>) => {
    const disabled = "disabled" in button ? button.disabled : undefined
    if (disabled != null && evaluateBooleanExpression(disabled, vars)) return true
    if (props.shouldDisableButton && props.shouldDisableButton(button)) return true
    return false
  }

  const dispatch = (actions?: z.infer<T>[]) => {
    if (!actions) return
    return props.dispatch(actions)
  }

  const interpolatedButtons = useMemo(() =>
    props.buttons.map(b => interpolatedButtonConfig(b, vars)), [ props.buttons, vars ])

  const colorsCssVars = useWidgetColorsCssVars({
    toolbarBorderBottomWidth: "0"
  })

  return (
    <div style={colorsCssVars}
         className={mergeClasses("flex flex-row flex-shrink-0", {
           "w-full gap-1 overflow-hidden": props.asToolbar,
           "p-1 gap-1.5": props.asToolbar && props.variant === "large",
           "p-1 ": props.asToolbar && props.variant === "default",
           "p-1  ": props.asToolbar && props.variant === "medium",
           "p-0.5": props.asToolbar && props.variant === "compact",
           "bg-[var(--w-bg-color)]": props.asToolbar,
           "[border-color:var(--w-border-color)] [border-bottom-width:var(--w-toolbar-border-bottom-width)]": props.asToolbar
         })}>
      {currentConfirmation && ( // Rendering the Dialog is moved up, since it does not work well nested in Popover
        <ConfirmationDialog open
                            title={currentConfirmation.title}
                            onConfirmed={currentConfirmation.onConfirmed}
                            onClose={() => setCurrentConfirmation(null)}
                            config={currentConfirmation.config} />
      )}

      <SizeVariantContext.Provider value={props.variant}>
        {interpolatedButtons.map((button, idx) => {
          const { tooltip, label } = getTooltipAndLabel(button, props.asToolbar ? "labeled" : "icon-only")

          if (shouldHideButton(button)) return null

          const disabled = shouldDisableButton(button)

          if (isSeparator(button)) return <HorizontalSeparator key={idx} />

          if (isSpacer(button)) return <div className="flex-grow" key={idx} />

          if (isHeader(button)) return <ToolHeader key={idx}
                                                   logo={button.logo}
                                                   title={button.title}
                                                   color={button.color} />

          if (isDropdown(button)) {
            return (
              // Only show loading state in the top item, not dropdown (because you won't be able to see that)
              <MutationWrapper key={idx} dispatch={dispatch}>
                {mutation => (
                  <DropdownToolButton icon={(!button.icon && !props.asToolbar) ? DotsHorizontalIcon : button.icon}
                                      contentClassName={props.popoverContentClassName}
                                      contentStyle={props.popoverContentStyle}
                                      withChevron={props.asToolbar}
                                      loading={mutation.isPending}
                                      label={label}
                                      title={tooltip}>
                    <RenderDropdownItemsRecursively
                      buttons={button.buttons!}
                      dispatch={mutation.mutate}
                      shouldDisableButton={shouldDisableButton}
                      shouldHideButton={shouldHideButton}
                      setCurrentConfirmation={setCurrentConfirmation}
                      popoverContentClassName={props.popoverContentClassName}
                      popoverContentStyle={props.popoverContentStyle} />
                  </DropdownToolButton>
                )}
              </MutationWrapper>
            )
          } else if (isButton(button) && button.confirmation && !disabled) {
            return (
              <MutationWrapper key={idx} dispatch={dispatch}>
                {mutation => (
                  <ConfirmationPopover
                    config={button.confirmation!}
                    title={tooltip}
                    onConfirmed={() => mutation.mutate(button.actions)}>
                    <ToolButton title={tooltip}
                                aria-label={label}
                                icon={button.icon}
                                animated
                                labeled={Boolean(label)}
                                loading={mutation.isPending}
                                disabled={disabled}>
                      {label}
                    </ToolButton>
                  </ConfirmationPopover>
                )}
              </MutationWrapper>
            )
          } else if (isButton(button)) {
            return (
              <MutationWrapper key={idx} dispatch={dispatch}>
                {mutation => (
                  <ToolButton title={tooltip}
                              icon={button.icon}
                              aria-label={label}
                              animated
                              labeled={Boolean(label)}
                              onClick={() => !disabled && mutation.mutate(button.actions)}
                              loading={mutation.isPending}
                              disabled={disabled}>
                    {label}
                  </ToolButton>
                )}
              </MutationWrapper>

            )
          } else {
            return null
          }
        })}
      </SizeVariantContext.Provider>
    </div>
  )
}

function MutationWrapper<T>(props: {
  dispatch: (any: T) => Promise<void> | void
  children: (m: UseMutationResult<void, Error, T, unknown>) => React.ReactNode
}) {
  const mutation = useMutation({
    mutationFn: async (any: T) => {
      const ret = props.dispatch(any)
      if (typeof ret === "object" && "then" in ret) return ret
    }
  })
  return props.children(mutation)
}

function RenderDropdownItemsRecursively<T extends z.ZodTypeAny>(props: {
  buttons: ButtonConfig<T>[]
  shouldHideButton: (btn: ButtonConfig<T>) => boolean
  shouldDisableButton: (btn: ButtonConfig<T>) => boolean
  dispatch: (actions?: z.infer<T>[]) => void
  setCurrentConfirmation: (state: ConfirmDialogState | null) => void
  popoverContentClassName?: string
  popoverContentStyle?: React.CSSProperties
}) {
  const { buttons, ...context } = props
  return (
    <>
      {props.buttons.map((subButton, idx) => {
        const { label } = getTooltipAndLabel(subButton, "labeled")

        if (props.shouldHideButton(subButton)) return null
        const disabled = props.shouldDisableButton(subButton)

        if (isSeparator(subButton)) return <VerticalSeparator key={idx} />

        if (isDropdown(subButton)) {
          return (
            <DropdownToolSubmenu key={idx}
                                 icon={subButton.icon}
                                 label={label}
                                 contentClassName={props.popoverContentClassName}
                                 contentStyle={props.popoverContentStyle}>
              <RenderDropdownItemsRecursively
                buttons={subButton.buttons}
                {...context}
              />
            </DropdownToolSubmenu>
          )
        }

        if (isButton(subButton) && subButton.confirmation && !disabled) {
          return (
            <DropdownToolItem key={idx}
                              icon={subButton.icon}
                              aria-label={label}
                              onSelect={e => {
                                props.setCurrentConfirmation({
                                  config: subButton.confirmation!,
                                  title: subButton.label,
                                  onConfirmed: () => props.dispatch(subButton.actions)
                                })
                              }}>
              {label}
            </DropdownToolItem>
          )
        } else if (isButton(subButton)) {
          return (
            <DropdownToolItem key={idx}
                              icon={subButton.icon}
                              aria-label={label}
                              onSelect={() => !disabled && props.dispatch(subButton.actions)}
                              disabled={disabled}>
              {label}
            </DropdownToolItem>
          )
        } else {
          return null
        }
      })}
    </>
  )
}

function getTooltipAndLabel<T extends z.ZodTypeAny>(button: ButtonConfig<T>, displayAs: "icon-only" | "labeled") {
  if (!isButton(button) && !isDropdown(button)) {
    return { tooltip: undefined, label: undefined }
  } else if (displayAs === "icon-only") {
    // Previously "label" was overloaded (also used for tooltip), so fall back to it for tooltip
    return { tooltip: button.tooltip ?? button.label, label: undefined } as const
  } else {
    return { tooltip: button.tooltip, label: button.label } as const
  }
}

function evaluateBooleanExpression(expression: string | boolean | undefined, rowAndAppVars: PublishedDict): boolean {
  if (typeof expression === 'boolean') return expression
  if (!expression) return false
  try {
    const resolvedValue = resolveTemplateStringAndLogErrors(expression, rowAndAppVars)
    if (resolvedValue.toLowerCase() === 'true') return true
    if (resolvedValue.toLowerCase() === 'false') return false
    if (resolvedValue === '') return false
    return true
  } catch (e) {
    console.error('Error evaluating action column button boolean expression:', e)
    return false
  }
}

/**
 * Fields resolved as templates while rendering, per button type. collectTemplateTopics subscribes to the
 * variables these read, so make a field templatable by adding it here, not by calling resolveTemplateString on it.
 */
const INTERPOLATED_FIELDS = {
  button: [ "icon", "label", "tooltip" ],
  dropdown: [ "icon", "label", "tooltip" ],
  header: [ "title" ],
} as const satisfies {
  button: readonly (keyof ButtonCfg<z.ZodTypeAny>)[]
  dropdown: readonly (keyof DropdownCfg<z.ZodTypeAny>)[]
  header: readonly (keyof HeaderCfg)[]
}

/** Fields evaluated as boolean expressions by shouldHideButton/shouldDisableButton, on any button type. */
const EXPRESSION_FIELDS = [ "hidden", "disabled" ] as const satisfies readonly (keyof ButtonCfg<z.ZodTypeAny>)[]

// This could be expanded, and possibly merged with evaluateBooleanExpression
function interpolatedButtonConfig<T extends z.ZodTypeAny>(button: ConfigurableButton<T>, vars: PublishedDict): ConfigurableButton<T> {
  if (isButton(button)) return interpolateFields(button, INTERPOLATED_FIELDS.button, vars)
  if (isDropdown(button)) return {
    ...interpolateFields(button, INTERPOLATED_FIELDS.dropdown, vars),
    buttons: (button.buttons || []).map(b => interpolatedButtonConfig(b, vars)),
  }
  if (isHeader(button)) return interpolateFields(button, INTERPOLATED_FIELDS.header, vars)
  return button
}

/** Resolves the given fields as templates; empty or missing values become undefined. */
function interpolateFields<B extends object>(button: B, fields: readonly (keyof B)[], vars: PublishedDict): B {
  const result = { ...button }
  for (const field of fields) {
    const value = button[field]
    result[field] = (typeof value === "string" && value ? resolveTemplateString(value, vars) : undefined) as B[keyof B]
  }
  return result
}

/** App-variable topics read while rendering the buttons: their INTERPOLATED_FIELDS and EXPRESSION_FIELDS. */
function collectTemplateTopics<T extends z.ZodTypeAny>(buttons: ButtonConfig<T>[]): string[] {
  const topics = new Set<string>()
  const add = (value: unknown) => {
    if (typeof value === "string") getPlaceholderTopics(value).forEach(t => topics.add(t))
  }
  const visit = (button: ButtonConfig<T>) => {
    if (isButton(button)) {
      INTERPOLATED_FIELDS.button.forEach(field => add(button[field]))
    } else if (isDropdown(button)) {
      INTERPOLATED_FIELDS.dropdown.forEach(field => add(button[field]))
      for (const child of button.buttons || []) visit(child)
    } else if (isHeader(button)) {
      INTERPOLATED_FIELDS.header.forEach(field => add(button[field]))
    }
    EXPRESSION_FIELDS.forEach(field => add((button as Record<string, unknown>)[field]))
  }
  buttons.forEach(visit)
  return [ ...topics ]
}

export function countVisibleButtons<T extends z.ZodTypeAny>(
  buttons: ButtonConfig<T>[],
  vars: PublishedDict,
  shouldHideButton?: (btn: ButtonConfig<T>) => boolean,
): number {
  return countInner(buttons)

  function countInner(buttons: ButtonConfig<T>[]): number {
    let count = 0
    for (const button of buttons) {
      const hidden = "hidden" in button ? button.hidden : undefined
      if (hidden != null && evaluateBooleanExpression(hidden, vars)) continue
      if (shouldHideButton?.(button)) continue

      if (isDropdown(button) && Array.isArray(button.buttons)) {
        count += countInner(button.buttons as any)
      } else if (!isSeparator(button) && !isSpacer(button)) {
        count++
      }
    }
    return count
  }
}

